// Prislista, tjänstebeskrivning och kundvillkor för affärsområdet Foto.
// Används på /foto och på ortssidorna så att en ortssida är en riktig
// säljsida — inte bara en samling kortlänkar.

import { Link } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import {
  FOTO_PRICES,
  FOTO_PRICE_SOURCE,
  FOTO_PROCESS,
  FOTO_TERMS,
  fotoCityPrice,
} from "@/content/foto-commerce";

const kr = (n: number) => `${n.toLocaleString("sv-SE")} kr`;

export function FotoPriceList({ city, id = "prislista" }: { city?: string; id?: string }) {
  const idx = fotoCityPrice(FOTO_PRICES[0], city).index;
  return (
    <Section id={id} className="bg-surface/30">
      <EyebrowHeading
        eyebrow="Prislista"
        title={city ? `Vad fotografering kostar i ${city}` : "Vad fotografering kostar hos oss"}
        lede={
          city && idx !== 100
            ? `Spannen nedan är justerade för ${city}, som ligger på index ${idx} mot riksmedianen enligt Bildindex 2026. Alla priser är exkl. moms och varje uppdrag offereras med fast pris innan bokning.`
            : "Spannen kommer ur vår egen studie Bildindex 2026 och gäller exkl. moms. Varje uppdrag offereras med fast pris innan bokning."
        }
      />

      <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[720px] border-collapse text-left text-sm">
          <caption className="sr-only">
            Prisspann, tid på plats och leveranstid per fototjänst{city ? ` i ${city}` : ""}
          </caption>
          <thead className="bg-surface/60 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">Tjänst</th>
              <th scope="col" className="px-4 py-3 font-medium">Prisspann (exkl. moms)</th>
              <th scope="col" className="px-4 py-3 font-medium">Tid på plats</th>
              <th scope="col" className="px-4 py-3 font-medium">Leverans</th>
              <th scope="col" className="px-4 py-3 font-medium">Antal bilder</th>
            </tr>
          </thead>
          <tbody>
            {FOTO_PRICES.map((r) => {
              const p = fotoCityPrice(r, city);
              return (
                <tr key={r.path} className="border-t border-white/10 align-top">
                  <th scope="row" className="px-4 py-4 font-medium">
                    <a
                      href={r.path}
                      className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                    >
                      {r.label}
                    </a>
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                      {r.includes}
                    </span>
                  </th>
                  <td className="whitespace-nowrap px-4 py-4">
                    {kr(p.min)}–{kr(p.max)}
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{r.onSite}</td>
                  <td className="whitespace-nowrap px-4 py-4 text-muted-foreground">
                    {r.leadMin}–{r.leadMax} arbetsdagar
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{r.delivered}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        {FOTO_PRICE_SOURCE.note} Källa:{" "}
        <Link
          to={FOTO_PRICE_SOURCE.path}
          className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
        >
          {FOTO_PRICE_SOURCE.label}
        </Link>
        .
      </p>
    </Section>
  );
}

export function FotoServiceSpec({ city, id = "sa-gar-det-till" }: { city?: string; id?: string }) {
  return (
    <Section id={id}>
      <EyebrowHeading
        eyebrow="Tjänstebeskrivning"
        title={city ? `Så går en fotografering i ${city} till` : "Så går en fotografering till"}
        lede="Fem steg från förfrågan till levererade filer. Samma upplägg oavsett om vi fotograferar i studio eller bygger mobil studio hos er."
      />
      <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {FOTO_PROCESS.map((s) => (
          <li key={s.n} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
            <div className="text-sm text-mint">Steg {s.n}</div>
            <h3 className="mt-2 text-lg font-semibold">{s.title}</h3>
            <p className="mt-3 text-sm text-muted-foreground">{s.body}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export function FotoTermsSection({ id = "kundvillkor" }: { id?: string }) {
  return (
    <Section id={id} className="bg-surface/30">
      <EyebrowHeading
        eyebrow="Kundvillkor"
        title="Villkoren för fotouppdrag"
        lede="Det som annars står i det finstilta — här står det innan ni beställer."
      />
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {FOTO_TERMS.map((t) => (
          <div key={t.id} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
            <h3 className="text-lg font-semibold">{t.heading}</h3>
            <p className="mt-3 text-sm text-muted-foreground">{t.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
